import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "react-toastify";
import {
  Upload,
  FileText,
  Plus,
  RefreshCw,
  CheckCircle,
  AlertCircle,
  BookOpen,
  Tag,
  Calendar,
  Send,
  Download,
  Trash2,
  ChevronDown,
  ChevronUp,
  Copy,
  Clipboard,
  ChevronDown as ChevronDownIcon,
} from "lucide-react";
import { server } from "../server";

const AddQuestionsInBulk = () => {
  const [questions, setQuestions] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [jsonInput, setJsonInput] = useState("");
  const [activeTab, setActiveTab] = useState("upload");
  const [subjects, setSubjects] = useState([]);
  const [chapters, setChapters] = useState([]);
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setValue,
    getValues,
  } = useForm({
    defaultValues: {
      subjectId: "",
      chapterId: "",
      topicId: "",
      difficulty: "medium",
      tags: [],
      relevantExams: [],
      year: new Date().getFullYear(),
    },
  });

  // Watch settings fields
  const watchedSubjectId = watch("subjectId");
  const watchedChapterId = watch("chapterId");

  // Fetch subjects on component mount
  useEffect(() => {
    fetchSubjects();
  }, []);

  // Fetch chapters when subject changes
  useEffect(() => {
    if (watchedSubjectId) {
      fetchChapters(watchedSubjectId);
    } else {
      setChapters([]);
      setValue("chapterId", "");
      setValue("topicId", "");
    }
  }, [watchedSubjectId, setValue]);

  // Fetch topics when chapter changes
  useEffect(() => {
    if (watchedSubjectId && watchedChapterId) {
      fetchTopics(watchedSubjectId, watchedChapterId);
    } else {
      setTopics([]);
      setValue("topicId", "");
    }
  }, [watchedSubjectId, watchedChapterId, setValue]);

  const fetchSubjects = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${server}/subject/all`, {
        params: { pageSize: 100 },
      });
      setSubjects(data.data || []);
    } catch (error) {
      toast.error("Failed to fetch subjects");
      console.error("Error fetching subjects:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchChapters = async (subjectId) => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${server}/chapter/all`, {
        params: { subjectId, pageSize: 100 },
      });
      setChapters(data.data || []);
    } catch (error) {
      toast.error("Failed to fetch chapters");
      console.error("Error fetching chapters:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTopics = async (subjectId, chapterId) => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${server}/topics/all`, {
        params: { subjectId, chapterId, pageSize: 100 },
      });
      setTopics(data.data || []);
    } catch (error) {
      toast.error("Failed to fetch topics");
      console.error("Error fetching topics:", error);
    } finally {
      setLoading(false);
    }
  };

  const examOptions = [
    "JEE Main",
    "JEE Advanced",
    "NEET",
    "UPSC",
    "CAT",
    "GATE",
    "SSC",
    "Bank PO",
    "NDA NA",
  ];

  const difficultyOptions = [
    { value: "easy", label: "Easy" },
    { value: "medium", label: "Medium" },
    { value: "hard", label: "Hard" },
  ];

  const getSubjectName = (subjectId) => {
    const subject = subjects.find((s) => s.id === parseInt(subjectId));
    return subject ? subject.subject : "Unknown Subject";
  };

  const getChapterName = (chapterId) => {
    const chapter = chapters.find((c) => c.id === parseInt(chapterId));
    return chapter ? chapter.chapter : "Unknown Chapter";
  };

  const getTopicName = (topicId) => {
    const topic = topics.find((t) => t.id === parseInt(topicId));
    return topic ? topic.topic : "Unknown Topic";
  };

  const parseQuestions = (data) => {
    try {
      const questionsArray = Array.isArray(data) ? data : data.questions || [];

      if (questionsArray.length === 0) {
        toast.error("No questions found in the data");
        return [];
      }

      const validatedQuestions = questionsArray.map((q, index) => ({
        id: `temp-${Date.now()}-${index}`,
        question: q.question || "",
        answer: q.answer || "",
        options: Array.isArray(q.options) ? q.options : [],
        explanation: q.explanation || "",
        hint: q.hint || "",
        solution: q.solution || "",
        subjectId: q.subjectId ? Number(q.subjectId) : null,
        chapterId: q.chapterId ? Number(q.chapterId) : null,
        topicId: q.topicId ? Number(q.topicId) : null,
        difficulty: q.difficulty || "",
        year: q.year ? Number(q.year) : null,
        tags: Array.isArray(q.tags) ? q.tags : [],
        relevantExams: Array.isArray(q.relevantExams) ? q.relevantExams : [],
        ...q,
      }));

      return validatedQuestions;
    } catch (error) {
      console.error("Error parsing data:", error);
      throw new Error("Invalid JSON format");
    }
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setParsing(true);
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const content = e.target.result;
        const parsedData = JSON.parse(content);
        const validatedQuestions = parseQuestions(parsedData);

        setQuestions(validatedQuestions);
        toast.success(
          `Successfully loaded ${validatedQuestions.length} questions`
        );
      } catch (error) {
        console.error("Error parsing JSON:", error);
        toast.error(error.message || "Invalid JSON file format");
      } finally {
        setParsing(false);
      }
    };

    reader.onerror = () => {
      toast.error("Error reading file");
      setParsing(false);
    };

    reader.readAsText(file);
  };

  const handleJsonPaste = () => {
    if (!jsonInput.trim()) {
      toast.error("Please paste JSON data first");
      return;
    }

    setParsing(true);
    try {
      const parsedData = JSON.parse(jsonInput);
      const validatedQuestions = parseQuestions(parsedData);

      setQuestions(validatedQuestions);
      setJsonInput("");
      toast.success(
        `Successfully loaded ${validatedQuestions.length} questions`
      );
    } catch (error) {
      console.error("Error parsing JSON:", error);
      toast.error(error.message || "Invalid JSON format");
    } finally {
      setParsing(false);
    }
  };

  const onSubmit = async (fixedData) => {
    if (questions.length === 0) {
      toast.error("Please upload or paste questions first");
      return;
    }

    setUploading(true);
    let successCount = 0;
    let errorCount = 0;

    try {
      const batchSize = 5;
      for (let i = 0; i < questions.length; i += batchSize) {
        const batch = questions.slice(i, i + batchSize);

        const batchPromises = batch.map(async (question) => {
          try {
            const payload = {
              ...question,
              ...fixedData,
              // Override with question-specific data if provided
              subjectId: question.subjectId || fixedData.subjectId,
              chapterId: question.chapterId || fixedData.chapterId,
              topicId: question.topicId || fixedData.topicId,
              difficulty: question.difficulty || fixedData.difficulty,
              year: question.year || fixedData.year,
              tags: [
                ...new Set([
                  ...(fixedData.tags || []),
                  ...(question.tags || []),
                ]),
              ],
              relevantExams: [
                ...new Set([
                  ...(fixedData.relevantExams || []),
                  ...(question.relevantExams || []),
                ]),
              ],
            };

            await axios.post(`${server}/question/new`, payload, {
              headers: {
                Authorization: `Bearer ${localStorage.getItem("token")}`,
              },
            });
            successCount++;
          } catch (error) {
            console.error("Error uploading question:", error);
            errorCount++;
          }
        });

        await Promise.all(batchPromises);

        if (i + batchSize < questions.length) {
          await new Promise((resolve) => setTimeout(resolve, 1000));
        }
      }

      if (successCount > 0) {
        toast.success(`Successfully uploaded ${successCount} questions`);
      }
      if (errorCount > 0) {
        toast.error(`Failed to upload ${errorCount} questions`);
      }

      if (errorCount === 0) {
        setQuestions([]);
        reset();
      }
    } catch (error) {
      console.error("Bulk upload error:", error);
      toast.error("Failed to upload questions");
    } finally {
      setUploading(false);
    }
  };

  const clearQuestions = () => {
    setQuestions([]);
    setJsonInput("");
  };

  const downloadTemplate = () => {
    const template = {
      questions: [
        {
          question: "What is the capital of France?",
          answer: "Paris",
          options: ["London", "Paris", "Berlin", "Madrid"],
          explanation: "Paris is the capital and most populous city of France.",
          hint: "Think of the Eiffel Tower",
          solution:
            "The capital of France is Paris, located in the north-central part of the country.",
          tags: ["geography", "europe"],
          relevantExams: ["UPSC", "SSC"],
        },
        {
          question: "Solve for x: 2x + 5 = 15",
          answer: "5",
          options: ["3", "5", "7", "10"],
          explanation: "Subtract 5 from both sides, then divide by 2.",
          solution: "2x + 5 = 15 → 2x = 10 → x = 5",
          tags: ["algebra", "equations"],
          relevantExams: ["JEE Main", "SSC"],
        },
      ],
    };

    const blob = new Blob([JSON.stringify(template, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "questions-template.json";
    link.click();
    URL.revokeObjectURL(url);
  };

  const copyTemplate = () => {
    const template = {
      questions: [
        {
          question: "What is the capital of France?",
          answer: "Paris",
          options: ["London", "Paris", "Berlin", "Madrid"],
          explanation: "Paris is the capital and most populous city of France.",
        },
      ],
    };

    navigator.clipboard.writeText(JSON.stringify(template, null, 2));
    toast.success("Template copied to clipboard!");
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.6,
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut",
      },
    },
  };

  return (
    <motion.div
      className="max-w-6xl mx-auto p-6 space-y-8 bg-gray-900 min-h-screen"
      initial="hidden"
      animate="visible"
      variants={containerVariants}
    >
      {/* Header */}
      <motion.div className="text-center space-y-3" variants={itemVariants}>
        <div className="flex items-center justify-center gap-3">
          <motion.div
            variants={itemVariants}
            className="p-3 bg-gradient-to-br from-blue-600 to-purple-600 rounded-2xl shadow-lg shadow-blue-500/20"
          >
            <Upload className="w-7 h-7 text-white" />
          </motion.div>
          <h1 className="text-4xl font-bold bg-gradient-to-br from-blue-400 to-purple-400 bg-clip-text text-transparent">
            Bulk Upload Questions
          </h1>
        </div>
        <p className="text-gray-400 text-lg">
          Upload multiple questions via JSON file or paste JSON directly
        </p>
      </motion.div>

      {/* Question Filters */}
      <motion.div
        className="bg-gray-800 rounded-xl p-6 border border-gray-700"
        variants={itemVariants}
      >
        <h2 className="text-lg font-semibold text-white mb-4">
          Question Filters
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Subject Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Subject *
            </label>
            <div className="relative">
              <select
                value={watch("subjectId")}
                onChange={(e) => {
                  setValue("subjectId", e.target.value);
                  setValue("chapterId", "");
                  setValue("topicId", "");
                }}
                className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-blue-500 transition-colors appearance-none"
              >
                <option value="">Select Subject</option>
                {subjects.map((subject) => (
                  <option key={subject.id} value={subject.id}>
                    {subject.subject}
                  </option>
                ))}
              </select>
              <ChevronDownIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
            </div>
          </div>

          {/* Chapter Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Chapter
            </label>
            <div className="relative">
              <select
                value={watch("chapterId")}
                onChange={(e) => {
                  setValue("chapterId", e.target.value);
                  setValue("topicId", "");
                }}
                disabled={!watch("subjectId")}
                className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-blue-500 transition-colors appearance-none disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <option value="">Select Chapter</option>
                {chapters.map((chapter) => (
                  <option key={chapter.id} value={chapter.id}>
                    {chapter.chapter}
                  </option>
                ))}
              </select>
              <ChevronDownIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
            </div>
          </div>

          {/* Topic Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Topic
            </label>
            <div className="relative">
              <select
                value={watch("topicId")}
                onChange={(e) => setValue("topicId", e.target.value)}
                disabled={!watch("subjectId") || !watch("chapterId")}
                className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-blue-500 transition-colors appearance-none disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <option value="">Select Topic</option>
                {topics.map((topic) => (
                  <option key={topic.id} value={topic.id}>
                    {topic.topic}
                  </option>
                ))}
              </select>
              <ChevronDownIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
            </div>
          </div>
        </div>
      </motion.div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
        {/* Left Column - File Upload & JSON Paste */}
        <div className="xl:col-span-2 space-y-6">
          {/* Tabs for Upload/Paste */}
          <motion.div
            className="bg-gray-800 border border-gray-700 rounded-xl"
            variants={itemVariants}
          >
            <div className="flex border-b border-gray-700">
              <button
                onClick={() => setActiveTab("upload")}
                className={`flex-1 py-4 px-6 font-semibold transition-colors ${
                  activeTab === "upload"
                    ? "text-blue-400 border-b-2 border-blue-400"
                    : "text-gray-400 hover:text-gray-300"
                }`}
              >
                <div className="flex items-center gap-2 justify-center">
                  <Upload size={18} />
                  Upload JSON File
                </div>
              </button>
              <button
                onClick={() => setActiveTab("paste")}
                className={`flex-1 py-4 px-6 font-semibold transition-colors ${
                  activeTab === "paste"
                    ? "text-green-400 border-b-2 border-green-400"
                    : "text-gray-400 hover:text-gray-300"
                }`}
              >
                <div className="flex items-center gap-2 justify-center">
                  <Clipboard size={18} />
                  Paste JSON
                </div>
              </button>
            </div>

            <div className="p-6">
              {activeTab === "upload" && (
                <div className="space-y-4">
                  <div className="border-2 border-dashed border-gray-600 rounded-lg p-8 text-center hover:border-blue-500 transition-colors">
                    <input
                      type="file"
                      accept=".json"
                      onChange={handleFileUpload}
                      className="hidden"
                      id="json-upload"
                    />
                    <label
                      htmlFor="json-upload"
                      className="cursor-pointer block"
                    >
                      <Upload className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                      <p className="text-gray-300 font-medium text-lg">
                        Click to upload JSON file
                      </p>
                      <p className="text-gray-500 text-sm mt-2">
                        Supports .json files with questions array
                      </p>
                    </label>
                  </div>

                  {parsing && (
                    <div className="flex items-center justify-center gap-2 text-blue-400">
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      Parsing JSON file...
                    </div>
                  )}
                </div>
              )}

              {activeTab === "paste" && (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-300">
                      Paste your JSON questions here:
                    </label>
                    <textarea
                      value={jsonInput}
                      onChange={(e) => setJsonInput(e.target.value)}
                      placeholder={`Paste your questions JSON here...\n\nExample:\n{\n  "questions": [\n    {\n      "question": "Your question?",\n      "answer": "Correct answer",\n      "options": ["Option A", "Option B", "Option C", "Option D"],\n      "explanation": "Detailed explanation of the answer"\n    }\n  ]\n}`}
                      className="w-full h-64 px-4 py-3 border border-gray-600 rounded-lg bg-gray-700 text-white placeholder-gray-400 focus:border-green-500 focus:ring-1 focus:ring-green-500 font-mono text-sm resize-none"
                    />
                  </div>

                  <div className="flex gap-3">
                    <button
                      onClick={handleJsonPaste}
                      disabled={parsing || !jsonInput.trim()}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {parsing ? (
                        <>
                          <RefreshCw className="w-4 h-4 animate-spin" />
                          Parsing...
                        </>
                      ) : (
                        <>
                          <Clipboard className="w-4 h-4" />
                          Parse JSON
                        </>
                      )}
                    </button>
                    <button
                      onClick={copyTemplate}
                      className="flex items-center gap-2 px-4 py-3 border border-gray-600 text-gray-300 rounded-lg hover:bg-gray-700 transition-colors"
                    >
                      <Copy size={16} />
                      Copy Template
                    </button>
                  </div>
                </div>
              )}

              {/* Questions Status */}
              {questions.length > 0 && (
                <div className="mt-4 bg-green-500/20 border border-green-500/30 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-green-400">
                    <CheckCircle className="w-5 h-5" />
                    <span className="font-semibold">
                      {questions.length} questions loaded
                    </span>
                  </div>
                  <button
                    onClick={() => setPreviewOpen(!previewOpen)}
                    className="text-green-300 text-sm mt-2 flex items-center gap-1 hover:text-green-200"
                  >
                    {previewOpen ? (
                      <ChevronUp size={16} />
                    ) : (
                      <ChevronDown size={16} />
                    )}
                    {previewOpen ? "Hide" : "Show"} Preview
                  </button>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-3 mt-4">
                <button
                  onClick={downloadTemplate}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-3 border border-gray-600 text-gray-300 rounded-lg hover:bg-gray-700 transition-colors"
                >
                  <Download size={18} />
                  Download Template
                </button>
                {questions.length > 0 && (
                  <button
                    onClick={clearQuestions}
                    className="flex items-center gap-2 px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                  >
                    <Trash2 size={18} />
                    Clear All
                  </button>
                )}
              </div>
            </div>
          </motion.div>

          {/* Questions Preview */}
          {previewOpen && questions.length > 0 && (
            <motion.div
              className="bg-gray-800 border border-gray-700 rounded-xl p-6"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
            >
              <h3 className="text-lg font-semibold text-white mb-4">
                Questions Preview ({questions.length})
              </h3>
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {questions.slice(0, 10).map((q, index) => (
                  <div
                    key={q.id}
                    className="bg-gray-700 rounded-lg p-4 border border-gray-600"
                  >
                    <div className="flex items-start gap-3">
                      <span className="bg-blue-500 text-white text-xs px-2 py-1 rounded-full min-w-6 text-center">
                        {index + 1}
                      </span>
                      <div className="flex-1">
                        <p className="text-gray-300 text-sm font-medium mb-2">
                          {q.question}
                        </p>
                        <div className="grid grid-cols-2 gap-2 text-xs text-gray-400">
                          <span>
                            Answer:{" "}
                            <strong className="text-green-400">
                              {q.answer}
                            </strong>
                          </span>
                          <span>Options: {q.options?.length || 0}</span>
                          {q.subjectId && (
                            <span>Subject: {getSubjectName(q.subjectId)}</span>
                          )}
                          {q.difficulty && (
                            <span>Difficulty: {q.difficulty}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                {questions.length > 10 && (
                  <div className="text-center text-gray-500 text-sm py-2">
                    + {questions.length - 10} more questions
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </div>

        {/* Right Column - Fixed Metadata & Upload */}
        <div className="space-y-6">
          {/* Fixed Metadata Form */}
          <motion.div
            className="bg-gray-800 border border-gray-700 rounded-xl p-6"
            variants={itemVariants}
          >
            <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-green-400" />
              Fixed Metadata
            </h2>
            <p className="text-gray-400 text-sm mb-4">
              This data will be applied to all uploaded questions
            </p>

            <div className="space-y-4">
              {/* Subject */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Subject *
                </label>
                <div className="relative">
                  <select
                    value={watchedSubjectId}
                    onChange={(e) => {
                      setValue("subjectId", e.target.value);
                      setValue("chapterId", "");
                      setValue("topicId", "");
                    }}
                    className="w-full px-3 py-2 border border-gray-600 rounded-lg bg-gray-700 text-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500 appearance-none"
                  >
                    <option value="">Select Subject</option>
                    {subjects.map((subject) => (
                      <option key={subject.id} value={subject.id}>
                        {subject.subject}
                      </option>
                    ))}
                  </select>
                  <ChevronDownIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
                </div>
              </div>

              {/* Chapter */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Chapter
                </label>
                <div className="relative">
                  <select
                    value={watchedChapterId}
                    onChange={(e) => {
                      setValue("chapterId", e.target.value);
                      setValue("topicId", "");
                    }}
                    disabled={!watchedSubjectId}
                    className="w-full px-3 py-2 border border-gray-600 rounded-lg bg-gray-700 text-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500 appearance-none disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <option value="">Select Chapter</option>
                    {chapters.map((chapter) => (
                      <option key={chapter.id} value={chapter.id}>
                        {chapter.chapter}
                      </option>
                    ))}
                  </select>
                  <ChevronDownIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
                </div>
              </div>

              {/* Topic */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Topic
                </label>
                <div className="relative">
                  <select
                    value={watch("topicId")}
                    onChange={(e) => setValue("topicId", e.target.value)}
                    disabled={!watchedSubjectId || !watchedChapterId}
                    className="w-full px-3 py-2 border border-gray-600 rounded-lg bg-gray-700 text-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500 appearance-none disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <option value="">Select Topic</option>
                    {topics.map((topic) => (
                      <option key={topic.id} value={topic.id}>
                        {topic.topic}
                      </option>
                    ))}
                  </select>
                  <ChevronDownIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
                </div>
              </div>

              {/* Difficulty */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Difficulty
                </label>
                <select
                  className="w-full px-3 py-2 border border-gray-600 rounded-lg bg-gray-700 text-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  {...register("difficulty")}
                >
                  {difficultyOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Year */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Year
                </label>
                <input
                  type="number"
                  className="w-full px-3 py-2 border border-gray-600 rounded-lg bg-gray-700 text-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  {...register("year", { valueAsNumber: true })}
                />
              </div>

              {/* Relevant Exams */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Relevant Exams
                </label>
                <div className="grid grid-cols-2 gap-2 max-h-32 overflow-y-auto p-2 bg-gray-750 rounded-lg">
                  {examOptions.map((exam) => (
                    <label
                      key={exam}
                      className="flex w-full items-center gap-2 p-2 hover:bg-gray-700 rounded"
                    >
                      <input
                        type="checkbox"
                        value={exam}
                        className="rounded w-6 border-gray-600 bg-gray-700 text-blue-500 focus:ring-blue-500"
                        {...register("relevantExams")}
                      />
                      <span className="text-sm w-full text-gray-300">
                        {exam}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>

          {/* Upload Summary & Action */}
          <motion.div
            className="bg-gray-800 border border-gray-700 rounded-xl p-6"
            variants={itemVariants}
          >
            <h2 className="text-xl font-semibold text-white mb-4">
              Upload Summary
            </h2>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="text-gray-400">Questions Ready:</div>
                <div className="text-white font-semibold text-lg">
                  {questions.length}
                </div>

                <div className="text-gray-400">Subject:</div>
                <div className="text-white">
                  {watchedSubjectId
                    ? getSubjectName(watchedSubjectId)
                    : "Not set"}
                </div>

                <div className="text-gray-400">Chapter:</div>
                <div className="text-white">
                  {watchedChapterId
                    ? getChapterName(watchedChapterId)
                    : "Not set"}
                </div>

                <div className="text-gray-400">Topic:</div>
                <div className="text-white">
                  {watch("topicId")
                    ? getTopicName(watch("topicId"))
                    : "Not set"}
                </div>

                <div className="text-gray-400">Exams:</div>
                <div className="text-white">
                  {watch("relevantExams")?.length || 0} selected
                </div>
              </div>

              <button
                onClick={handleSubmit(onSubmit)}
                disabled={uploading || questions.length === 0}
                className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-gradient-to-br from-blue-600 to-purple-600 text-white font-semibold rounded-xl hover:shadow-lg hover:shadow-blue-500/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {uploading ? (
                  <>
                    <RefreshCw className="w-5 h-5 animate-spin" />
                    Uploading {questions.length} Questions...
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    Upload {questions.length} Questions
                  </>
                )}
              </button>

              {questions.length > 0 && (
                <p className="text-gray-400 text-sm text-center">
                  Questions will be uploaded in batches of 5 to ensure stability
                </p>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </motion.div>
  );
};

export default AddQuestionsInBulk;
